import { useEffect, useState } from "react";

// GDPR/PECR-compliant lightweight cookie consent banner.
// - Blocks PostHog tracking until consent is granted.
// - Persists decision in localStorage under "uktda_consent".
// - Re-openable via window.dispatchEvent(new Event("uktda:open-consent")).
const STORAGE_KEY = "uktda_consent";

const stopPostHog = () => {
  if (typeof window === "undefined") return;
  const ph = window.posthog;
  if (ph && typeof ph.opt_out_capturing === "function") {
    try {
      ph.opt_out_capturing();
    } catch (_) {}
  }
};

const startPostHog = () => {
  if (typeof window === "undefined") return;
  const ph = window.posthog;
  if (ph && typeof ph.opt_in_capturing === "function") {
    try {
      ph.opt_in_capturing();
    } catch (_) {}
  }
};

export default function CookieConsent() {
  const [decision, setDecision] = useState("pending"); // pending | accepted | rejected

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === "accepted" || saved === "rejected") {
        setDecision(saved);
        if (saved === "accepted") startPostHog();
        else stopPostHog();
        return;
      }
      // No decision yet — block analytics by default
      stopPostHog();
    } catch (_) {
      stopPostHog();
    }

    const openHandler = () => setDecision("pending");
    window.addEventListener("uktda:open-consent", openHandler);
    return () => window.removeEventListener("uktda:open-consent", openHandler);
  }, []);

  const persist = (value) => {
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch (_) {}
    setDecision(value);
    if (value === "accepted") startPostHog();
    else stopPostHog();
  };

  if (decision !== "pending") return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      className="fixed inset-x-3 bottom-3 md:inset-auto md:bottom-5 md:left-5 md:right-5 lg:right-auto lg:max-w-xl z-[60] rounded-md border border-slate-200 bg-white shadow-xl p-5 md:p-6"
      data-testid="cookie-consent"
    >
      <p className="label-eyebrow text-safety">Your privacy</p>
      <h2 className="mt-2 font-display font-bold text-navy text-lg">We use cookies</h2>
      <p className="mt-2 text-sm text-slate-600 leading-relaxed">
        We use a small number of cookies to understand how visitors use this
        site so we can improve it. Strictly necessary cookies are always
        active. Analytics cookies only load if you accept. See our{" "}
        <a href="/legal/privacy" className="text-navy underline underline-offset-2 hover:text-safety">
          Privacy Policy
        </a>{" "}
        for detail.
      </p>
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => persist("accepted")}
          data-testid="cookie-accept"
          className="inline-flex items-center gap-2 bg-safety hover:bg-[#e66e00] text-white font-semibold text-sm px-4 py-2.5 rounded-md transition-colors"
        >
          Accept all
        </button>
        <button
          type="button"
          onClick={() => persist("rejected")}
          data-testid="cookie-reject"
          className="inline-flex items-center gap-2 border border-navy text-navy hover:bg-slate-50 font-semibold text-sm px-4 py-2.5 rounded-md transition-colors"
        >
          Reject non-essential
        </button>
      </div>
    </div>
  );
}
